import { Injectable } from "@angular/core";
import { AbstractControl, FormArray, FormGroup } from "@angular/forms";
import { FormItemModel } from "services/models/form-builder/form-item.model";
import { guidEmpty, isGuid } from "utils/helps";

@Injectable({ providedIn: "root" })
export class FormItemsOrganizerService {
  public isChild(ctrl: AbstractControl): boolean {
    return !!ctrl.get("isChild")?.value;
  }

  public getParentId(ctrl: AbstractControl): string | null {
    return ctrl.get("parentItem")?.value?.id ?? null;
  }

  public getParentIndexOf(
    controls: AbstractControl[],
    idx: number,
    isChildFn?: (ctrl: AbstractControl) => boolean
  ): number {
    const ctrl = controls[idx];
    if (!ctrl) return -1;
    const isChild = isChildFn ? isChildFn(ctrl) : this.isChild(ctrl);
    if (!isChild) return idx;
    const pid = this.getParentId(ctrl);
    return controls.findIndex((c) => c.get("id")?.value === pid);
  }

  public getBlockRangeByParentIndex(
    controls: AbstractControl[],
    parentIdx: number
  ): { start: number; end: number } {
    if (parentIdx < 0) return { start: -1, end: -1 };
    const parentId = controls[parentIdx].get("id")?.value;
    let end = parentIdx;
    for (let i = parentIdx + 1; i < controls.length; i++) {
      const c = controls[i];
      if (this.getParentId(c) === parentId) end = i;
      else break;
    }
    return { start: parentIdx, end };
  }

  public renumberSequential(itemsArray: FormArray): void {
    let order = 1;
    itemsArray.controls.forEach((ctrl) => ctrl.get("order")?.setValue(order++));
    itemsArray.updateValueAndValidity({ emitEvent: false });
  }

  public moveBlockOnDrop(
    itemsArray: FormArray,
    previousIndex: number,
    currentIndex: number,
    isChildFn?: (ctrl: AbstractControl) => boolean
  ): void {
    const controls = itemsArray.controls;
    const dragged = controls[previousIndex];
    if (!dragged) return;

    const isChild = isChildFn ? isChildFn(dragged) : this.isChild(dragged);
    if (isChild) return;

    const targetParentIdx = this.getParentIndexOf(
      controls,
      currentIndex,
      isChildFn
    );
    if (targetParentIdx !== -1) {
      const tRange = this.getBlockRangeByParentIndex(controls, targetParentIdx);
      const movingDown = currentIndex > previousIndex;
      if (currentIndex > tRange.start && currentIndex <= tRange.end) {
        currentIndex = movingDown ? tRange.end + 1 : tRange.start;
      }
    }

    const dRange = this.getBlockRangeByParentIndex(controls, previousIndex);
    const blockSize = dRange.end - dRange.start + 1;

    if (currentIndex >= dRange.start && currentIndex <= dRange.end) return;

    const block = controls.splice(dRange.start, blockSize);
    if (currentIndex > dRange.start) currentIndex -= blockSize;

    controls.splice(currentIndex, 0, ...block);

    this.renumberSequential(itemsArray);
  }

  public insertOrReposition(
    itemsArray: FormArray,
    item: FormGroup,
    options?: { oldControlRef?: AbstractControl | null }
  ): void {
    const controls = itemsArray.controls;
    const id = item.get("id")?.value as string | null;
    const isChild = !!item.get("isChild")?.value;
    const parent = item.get("parentItem")?.value as { id?: string } | null;

    let existingIndex = -1;
    if (options?.oldControlRef) {
      existingIndex = controls.indexOf(options.oldControlRef);
    }
    const isNewId = !id || !isGuid(id) || id === guidEmpty;
    if (existingIndex === -1 && id && !isNewId) {
      existingIndex = controls.findIndex((c) => c.get("id")?.value === id);
    }

    if (this.checkHasChangeParent(itemsArray, existingIndex, item)) {
      if (existingIndex !== -1 && !isChild) {
        itemsArray.setControl(existingIndex, item);
        this.renumberSequential(itemsArray);
        return;
      }

      if (existingIndex !== -1) {
        controls.splice(existingIndex, 1);
      }
    } else {
      if (existingIndex !== -1) {
        if (isChild) {
          controls[existingIndex].patchValue(item.value); // mantém ordem
        } else {
          itemsArray.setControl(existingIndex, item); // substitui
        }
        this.updateItens(itemsArray);
        this.renumberSequential(itemsArray);
        return;
      }
    }

    // Novo item
    if (isChild && parent?.id) {
      const parentIdx = controls.findIndex(
        (c) => c.get("id")?.value === parent.id
      );
      if (parentIdx === -1) {
        controls.push(item);
      } else {
        let insertIdx = parentIdx + 1;
        while (
          insertIdx < controls.length &&
          this.getParentId(controls[insertIdx]) === parent.id
        ) {
          insertIdx++;
        }
        controls.splice(insertIdx, 0, item);
      }
    } else {
      controls.push(item);
    }

    this.updateItens(itemsArray);
    this.renumberSequential(itemsArray);
  }

  public checkHasChangeParent(itemsArray: FormArray, index: number, item: FormGroup) {
    const newValue = item.value as FormItemModel;
    const oldValue = itemsArray.controls[index]?.value as FormItemModel | undefined;
    if (oldValue && (oldValue.parentItemId !== newValue.parentItemId)) {
      return true;
    } else {
      return false;
    }
  }

  public updateItens(itemsArray: FormArray): void {
    if (!itemsArray) return;

    const controls = itemsArray.controls;
    const values = controls.map((control) => control.value as FormItemModel);

    controls.forEach((control) => {
      const value = control.value as FormItemModel;
      value.parentItem =
        values.find((x) => x.id === value.parentItemId) || null;

      if (value.parentItem?.isChild) {
        this.clearParentRelation(control);
      }
    });

    itemsArray.updateValueAndValidity();
  }

  private clearParentRelation(control: AbstractControl): void {
    const updates = {
      parentItemId: null,
      parentItem: null,
      isChild: false,
    };

    Object.entries(updates).forEach(([key, value]) => {
      control.get(key)?.setValue(value);
    });

    control.updateValueAndValidity();
  }
}
